import { f as fetchHTML } from './index-Da0J-i1G.js';
import { pubsub } from '@services';
import { d as debounce } from './debounce-Dg7s1ivP.js';

class PredictiveSearchResults extends HTMLElement {
  abortController;

  connectedCallback() {
    this.options = {
      minimumSearchTermLength: 2,
      searchId: this.dataset.searchId,
    };

    // Get the search form
    this.search = document.querySelector(`#${this.options.searchId}`);
    if (!this.search) return;

    // Get the empty state menu
    this.emptyStateMenu = document.querySelector('[data-search-empty-state]');

    // Handle search input
    this.unsubscribeSearchInput = pubsub.subscribe(
      `search:input:${this.dataset.searchId}`,
      ({ value }) => {
        this.updateSearch(value);
      }
    );

    // When rendered in a drawer, subscribe to the drawer open-event to clear the input and results
    this.drawerId = this.closest('drawer-element')?.id;
    if (this.drawerId) {
      this.unsubscribeDrawerOpened = pubsub.subscribe(`drawer:opened:${this.drawerId}`, () => {
        this.search.clearSearchInput();
        this.clearSearchResults();
        this.toggleEmptyState(false);
      });
    }
  }

  disconnectedCallback() {
    this.unsubscribeSearchInput();
    if (this.drawerId) {
      this.unsubscribeDrawerOpened();
    }
  }

  /**
   * Clear the search results
   */
  clearSearchResults() {
    this.innerHTML = '';
  }

  /**
   * Clear the search input
   */
  onClearSearchInput() {
    this.ariaHidden = true;
    this.toggleEmptyState(false);
  }

  /**
   * Enable loaders
   */
  enableLoaders() {
    this.querySelectorAll('[data-loader]').forEach((loader) => {
      loader.ariaHidden = false;
    });
  }

  /**
   * Disable loaders
   */
  disableLoaders() {
    setTimeout(() => {
      this.querySelectorAll('[data-loader]').forEach((loader) => {
        loader.ariaHidden = true;
      });
    }, 50);
  }

  /**
   * Update the search
   */
  updateSearch = debounce((searchTerm) => this.getSearchResults(searchTerm), 300);

  /**
   * Get the search results
   */
  getSearchResults(searchTerm) {
    searchTerm = searchTerm.trim();
    if (searchTerm.length < this.options.minimumSearchTermLength) {
      this.clearSearchResults();
      this.toggleEmptyState(false);
      this.ariaHidden = true;
      return;
    }
    this.fetchSearchResults(searchTerm);
  }

  /**
   * Hide the empty state menu when the predictive search results are shown
   */
  toggleEmptyState(hide = true) {
    if (!this.emptyStateMenu) return;

    this.emptyStateMenu.classList.toggle('hidden', hide);
  }

  /**
   * Fetch the search results
   */
  async fetchSearchResults(searchTerm) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    this.enableLoaders();

    try {
      const searchUrl = window.routes.predictive_search_url || '/search/suggest';
      const url = `${searchUrl}?q=${searchTerm}&section_id=fetch-predictive-search`;

      const html = await fetchHTML(url, { signal: this.abortController.signal });
      const resultsMarkup = html.querySelector('predictive-search-results').innerHTML;
      this.innerHTML = '';
      this.innerHTML = resultsMarkup;
      this.ariaHidden = false;
      this.toggleEmptyState();
    } catch (error) {
      console.error('Error fetching search results:', error);
    } finally {
      this.disableLoaders();
    }
  }
}

customElements.define('predictive-search-results', PredictiveSearchResults);
